import { Dialog } from 'primereact/dialog';
import { type ReactNode } from 'react';

export interface BaseDialogProps {
  visible: boolean;
  onHide: () => void;
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  /** false: no ✕ (the dialog has its own Close button); Esc still closes it. */
  closable?: boolean;
  /** false: don't move focus to the first button on open (no ring on it straight away). */
  focusOnShow?: boolean;
  /**
   * false: a press on the background doesn't close it. Needed when the dialog asks for a decision,
   * and for one opened by a drawer's outside press: the drawer reacts to the mouse going down, so
   * the mouse coming up lands on this background and would close it again at once.
   */
  closeOnOutside?: boolean;
}

const WIDTH = { sm: '26rem', md: '34rem', lg: '48rem', xl: '54rem' } as const;

/** Centred modal. Esc and the ✕ both call `onHide`. */
export function BaseDialog({
  visible,
  onHide,
  header,
  children,
  footer,
  width = 'md',
  closable = true,
  focusOnShow = true,
  closeOnOutside = true,
}: BaseDialogProps) {
  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      footer={footer}
      modal
      closable={closable}
      focusOnShow={focusOnShow}
      dismissableMask={closeOnOutside}
      draggable={false}
      resizable={false}
      style={{ width: WIDTH[width], maxWidth: 'calc(100vw - 32px)' }}
    >
      {children}
    </Dialog>
  );
}
